import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { useMeasuredTriggerWidth } from '../../useMeasuredTriggerWidth'
import { Box } from '../box'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { Pressable } from '../pressable'
import {
    ChevronDownGlyph,
    CloseGlyph,
    GLYPH_COLORS,
    type GlyphTone,
} from '../primitive-glyphs/glyphs'
import { ScrollView } from '../scroll-view'
import { Text } from '../text'

export type StatusDropdownOption = {
    value: string
    label: string
    disabled?: boolean
}

export type StatusDropdownSize = 'sm' | 'md' | 'md-compact'

/**
 * `filter` is the collapsed form of a `FilterSwitch` (Figma
 * `FilterSwitchDropdown` 13:3870): the same elevation track with a single
 * surface-filled pill on it, used when the pills no longer fit the widget.
 */
export type StatusDropdownVariant = 'default' | 'filter'

export type StatusDropdownProps = {
    value: string
    options: StatusDropdownOption[]
    onChange: (value: string) => void
    disabled?: boolean
    placeholder?: string
    size?: StatusDropdownSize
    /**
     * Renders a clear "×" ahead of the chevron. Pass it only while there is
     * something to clear — the trigger shows the affordance whenever it is set.
     */
    onClear?: () => void
    clearAccessibilityLabel?: string
    variant?: StatusDropdownVariant
    /** Chrome ink for the chevron and clear glyphs. */
    glyphTone?: GlyphTone
    className?: string
    testID?: string
    nativeID?: string
}

const triggerSizeClasses: Record<StatusDropdownSize, string> = {
    'sm': 'h-7 gap-1 px-2',
    'md': 'h-10 gap-2 px-3',
    'md-compact': 'h-10 gap-3 px-3 py-2',
}
const labelSize = { 'sm': 'caption', 'md': 'body', 'md-compact': 'body' } as const
const labelWeight = { 'sm': 'regular', 'md': 'regular', 'md-compact': 'semiBold' } as const
const chevronSize: Record<StatusDropdownSize, number> = { 'sm': 16, 'md': 24, 'md-compact': 24 }

export const StatusDropdown: React.FC<StatusDropdownProps> = ({
    value,
    options,
    onChange,
    disabled = false,
    placeholder,
    size = 'sm',
    variant = 'default',
    onClear,
    clearAccessibilityLabel = 'Clear',
    glyphTone = 'default',
    className,
    testID,
    nativeID,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { triggerWidth, measureTriggerRef } = useMeasuredTriggerWidth()
    const selected = options.find(o => o.value === value)
    const isFilter = variant === 'filter'
    const glyphSize = isFilter ? 24 : chevronSize[size]

    const select = (next: string) => {
        onChange(next)
        setIsOpen(false)
    }

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            trapFocus={false}
            focusScope={false}
            placement='bottom left'
            trigger={triggerProps => {
                const { ref: popoverRef, ...rest } = triggerProps as {
                    ref?: React.Ref<unknown>
                }
                const toggle = () => setIsOpen(v => !v)
                const content = (
                    <>
                        <Pressable
                            nativeID={nativeID}
                            testID={testID}
                            disabled={disabled}
                            onPress={toggle}
                            accessibilityRole='button'
                            accessibilityState={{ disabled, expanded: isOpen }}
                            className='h-full min-w-0 flex-1 flex-row items-center'
                        >
                            {/* Clamped to one line: a caller can constrain the trigger's width
                                (`w-full min-w-0` inside a narrow table column), and without this a
                                long label wraps to two lines and spills out of the fixed height. */}
                            <Text
                                size={labelSize[size]}
                                weight={labelWeight[size]}
                                tone={selected ? 'default' : 'muted'}
                                numberOfLines={1}
                                className='min-w-0 shrink'
                            >
                                {selected?.label ?? placeholder}
                            </Text>
                        </Pressable>
                        {onClear && (
                            <Pressable
                                accessibilityRole='button'
                                accessibilityLabel={clearAccessibilityLabel}
                                disabled={disabled}
                                onPress={onClear}
                                className='h-6 w-6 shrink-0 items-center justify-center'
                            >
                                <CloseGlyph size={glyphSize} color={GLYPH_COLORS[glyphTone]} />
                            </Pressable>
                        )}
                        {/* Mouse-only hit area duplicating the trigger beside it: no role and
                            out of the tab order, so it is not announced as a second button. */}
                        <Pressable
                            focusable={false}
                            disabled={disabled}
                            onPress={toggle}
                            className='h-6 w-6 shrink-0 items-center justify-center'
                        >
                            <ChevronDownGlyph size={glyphSize} color={GLYPH_COLORS[glyphTone]} />
                        </Pressable>
                    </>
                )
                return (
                    <Box
                        {...rest}
                        ref={measureTriggerRef(popoverRef)}
                        className={twMerge(
                            isFilter
                                ? 'flex-row items-center self-start rounded-full bg-background-elevation p-1'
                                : twMerge(
                                      'min-w-[116px] flex-row items-center rounded border border-solid border-border-subtle bg-background-input hover:border-border-strong',
                                      triggerSizeClasses[size],
                                  ),
                            disabled && 'pointer-events-none opacity-40',
                            className,
                        )}
                    >
                        {isFilter ? (
                            <Box className='min-h-7 min-w-0 flex-1 flex-row items-center gap-0.5 rounded-full border border-solid border-border-strong bg-background-surface py-0.5 pl-3 pr-1 web:hover:bg-background-surface-hover'>
                                {content}
                            </Box>
                        ) : (
                            content
                        )}
                    </Box>
                )
            }}
        >
            <PopoverBackdrop />
            <PopoverContent className='w-auto max-w-none rounded border border-solid border-border-subtle bg-background-elevation py-1'>
                {/* The panel sizes to its widest row, with the trigger's width as the floor
                    (`w-auto` above drops the popover's own `w-full`). A fixed `width` here plus
                    a fixed `min-w` on the panel left a narrow table column with a menu wider
                    than the column whose rows still clipped their labels. */}
                {/* ScrollView capped at 240px, not a Box: long option lists (the ~40-airline
                    dashboard filter) otherwise grew the panel past the viewport with no way to
                    reach the rest. RN honours only visible/hidden for overflow, so a plain View
                    with `overflow-scroll` would clip the tail on native. */}
                <ScrollView
                    style={triggerWidth ? { minWidth: triggerWidth } : undefined}
                    className='max-h-[240px] self-stretch'
                >
                    {options.map(option => {
                        const isSelected = option.value === value
                        return (
                            <Pressable
                                key={option.value}
                                onPress={option.disabled ? undefined : () => select(option.value)}
                                disabled={option.disabled}
                                accessibilityRole='menuitem'
                                accessibilityState={{
                                    selected: isSelected,
                                    disabled: option.disabled,
                                }}
                                className={twMerge(
                                    'px-3 py-2',
                                    option.disabled
                                        ? 'pointer-events-none opacity-40'
                                        : isSelected
                                          ? 'bg-background-surface-hover'
                                          : 'hover:bg-background-surface-hover',
                                )}
                            >
                                <Text size='caption' tone='default'>
                                    {option.label}
                                </Text>
                            </Pressable>
                        )
                    })}
                </ScrollView>
            </PopoverContent>
        </Popover>
    )
}

StatusDropdown.displayName = 'StatusDropdown'
